import React, { useState, useRef } from "react";
import { GLBANOTICELINK } from "Utils/EnvVars";
import { faro } from "@grafana/faro-react";
import { Box, Link, Modal, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PRIVACY_POLICY_LINK, TERMS_OF_SERVICE_LINK } from "Utils/Constants";

export default function TermsAndConditions({
  buttonText = "submit",
  isCashier,
}) {
  const [modal, setModal] = useState({ open: false, url: "", title: "" });

  const styleClass = isCashier
    ? "cashier-termsAndConditions"
    : "termsAndConditions";

  const linkClick = (link) => faro.api.pushEvent(`User clicked on ${link}`);

  const openModal = (e, url, title) => {
    e.preventDefault();
    linkClick(title);
    initialLoadRef.current = true;
    isResettingRef.current = false;
    setModal({ open: true, url, title });
  };

  const handleCloseModal = () => setModal({ open: false, url: "", title: "" });

  const iframeRef = useRef(null);
  const initialLoadRef = useRef(true);
  const isResettingRef = useRef(false);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }

    if (isResettingRef.current) {
      isResettingRef.current = false;
      return;
    }

    try {
      const iframeOrigin = new URL(iframe.contentWindow.location.href).origin;
      const originalOrigin = new URL(modal.url).origin;
      if (iframeOrigin !== originalOrigin) {
        isResettingRef.current = true;
        iframe.src = modal.url;
      }
    } catch {
      isResettingRef.current = true;
      iframe.src = modal.url;
    }
  };

  return (
    <>
      <Modal
        open={modal.open}
        onClose={handleCloseModal}
        aria-labelledby="link-modal-title"
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Box
          sx={{
            width: "90vw",
            height: "85vh",
            maxWidth: "900px",
            background: "var(--color-surface-background-primary, #fff)",
            borderRadius: "8px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            outline: "none",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              borderBottom: "1px solid rgba(0,0,0,0.12)",
            }}
          >
            <Typography
              id="link-modal-title"
              sx={{ fontWeight: 600, fontSize: "16px" }}
            >
              {modal.title}
            </Typography>
            <IconButton
              onClick={handleCloseModal}
              aria-label={`Close ${modal.title}`}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: "hidden" }}>
            <iframe
              ref={iframeRef}
              src={modal.url}
              title={modal.title}
              width="100%"
              height="100%"
              style={{ border: "none", display: "block" }}
              sandbox="allow-scripts allow-same-origin"
              onLoad={handleIframeLoad}
            />
          </Box>
        </Box>
      </Modal>

      <Box
        className={styleClass}
        sx={{
          width: "100vw",
          display: "flex",
          position: isCashier ? "relative" : "fixed",
          marginTop: "50px",
          bottom: 0,
          left: 0,
          flexDirection: "column",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: "100%",
            padding: isCashier
              ? "16px var(--spacing-4xl) 16px var(--spacing-2xl)"
              : "16px var(--spacing-4xl)",
            background: "var(--color-surface-background-secondary)",
            textAlign: "center",
            fontSize: "var(--caption-size-medium)",
            fontWeight: 400,
          }}
        >
          By clicking {buttonText}, I agree to the{" "}
          <Link
            component="button"
            onClick={(e) =>
              openModal(e, TERMS_OF_SERVICE_LINK, "Terms & Conditions")
            }
            sx={{
              color: "var(--color-text-link)",
              fontWeight: 700,
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "inherit",
              verticalAlign: "baseline",
            }}
          >
            Terms & Conditions
          </Link>
          ,{" "}
          <Link
            component="button"
            onClick={(e) => openModal(e, PRIVACY_POLICY_LINK, "Privacy Notice")}
            sx={{
              color: "var(--color-text-link)",
              fontWeight: 700,
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "inherit",
              verticalAlign: "baseline",
            }}
          >
            Privacy Notice
          </Link>
          , and{" "}
          <Link
            component="button"
            onClick={(e) => openModal(e, GLBANOTICELINK, "GLBA Notice")}
            sx={{
              color: "var(--color-text-link)",
              fontWeight: 700,
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "inherit",
              verticalAlign: "baseline",
            }}
          >
            GLBA Notice
          </Link>
          .
          {isCashier && (
            <Box sx={{ marginTop: "12px", fontSize: "14px" }}>
              Secure deposit powered by PavilionPayments
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}
